const monthFormatter = new Intl.DateTimeFormat("pt-BR", {
  month: "2-digit",
  year: "numeric",
  timeZone: "UTC",
});

const addMonths = (date, amount) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + amount, 1));

const getMonthKey = (date) =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;

const parseMonth = (item) => {
  if (item.mes_ref) {
    const date = new Date(item.mes_ref);
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  }

  const [month, year] = String(item.mes_ano).split("/");
  return new Date(Date.UTC(Number(year), Number(month) - 1, 1));
};

const average = (values) =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

const weightedAverage = (values) => {
  const weightsTotal = (values.length * (values.length + 1)) / 2;
  return values.reduce((sum, value, index) => sum + value * (index + 1), 0) / weightsTotal;
};

/*
 * MAPE mede o erro percentual médio: média(|real - previsão| / real) × 100.
 * Exemplo: reais [100, 200] e forecasts [90, 220] geram erros [10%, 10%],
 * portanto MAPE = 10%. Meses com valor real zero não entram no cálculo.
 */
const calcularMape = (actualValues, fittedValues) => {
  const percentageErrors = actualValues
    .map((value, index) => value > 0 && Number.isFinite(fittedValues[index])
      ? Math.abs((value - fittedValues[index]) / value)
      : null)
    .filter((value) => value !== null);

  return percentageErrors.length ? average(percentageErrors) * 100 : null;
};

export const buildMonthlySeries = (items, startDate, endDate) => {
  if (!items.length) return [];

  const totals = new Map();
  items.forEach((item) => {
    const date = parseMonth(item);
    const key = getMonthKey(date);
    totals.set(key, (totals.get(key) || 0) + Number(item.soma || 0));
  });

  const result = [];
  const dates = items.map(parseMonth).sort((first, second) => first - second);
  let current = startDate
    ? new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), 1))
    : dates[0];
  const end = endDate
    ? new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), 1))
    : dates[dates.length - 1];

  while (current <= end) {
    result.push({
      date: current,
      label: monthFormatter.format(current),
      value: totals.get(getMonthKey(current)) || 0,
    });
    current = addMonths(current, 1);
  }

  return result;
};

/*
 * MMS usa a média dos últimos n meses. Exemplo com [100, 120, 140]:
 * MMS(3) = (100 + 120 + 140) / 3 = 120.
 * MMP aplica pesos crescentes aos meses recentes. Com pesos [1, 2, 3]:
 * MMP = (100×1 + 120×2 + 140×3) / 6 = 126,67.
 */
const calcularMediaMovel = (historicalValues, movingWindow, useWeights = false) => {
  const fittedValues = historicalValues.map((value, index) => {
    if (index < movingWindow) return null;
    const currentWindow = historicalValues.slice(index - movingWindow, index);
    return useWeights ? weightedAverage(currentWindow) : average(currentWindow);
  });
  const forecastValues = [];
  const extendedValues = [...historicalValues];

  return { fitted: fittedValues, forecast(forecastHorizon) {
    for (let index = 0; index < forecastHorizon; index += 1) {
      const currentWindow = extendedValues.slice(-movingWindow);
      const nextForecast = useWeights ? weightedAverage(currentWindow) : average(currentWindow);
      forecastValues.push(nextForecast);
      extendedValues.push(nextForecast);
    }
    return forecastValues;
  }};
};

/*
 * SES combina o dado recente com o forecast anterior:
 * F(t+1) = alpha × D(t) + (1 - alpha) × F(t).
 * Exemplo: alpha 0,3, dado atual 140 e forecast anterior 120:
 * F(t+1) = 0,3×140 + 0,7×120 = 126.
 */
const calcularSuavizacaoExponencial = (historicalValues, smoothingAlpha) => {
  if (!historicalValues.length) return { fitted: [], forecast: () => [] };
  const fittedValues = [historicalValues[0]];
  for (let index = 1; index < historicalValues.length; index += 1) {
    fittedValues[index] = smoothingAlpha * historicalValues[index - 1]
      + (1 - smoothingAlpha) * fittedValues[index - 1];
  }
  const nextForecast = smoothingAlpha * historicalValues[historicalValues.length - 1]
    + (1 - smoothingAlpha) * fittedValues[fittedValues.length - 1];
  return {
    fitted: fittedValues,
    forecast: (forecastHorizon) => Array(forecastHorizon).fill(nextForecast),
  };
};

/*
 * Tendência sazonal multiplica a regressão linear pelo índice do mês:
 * Forecast(t) = Trend(t) × seasonalIndex(mês).
 * Se a trend de março é 1.000 m e a média de março é 20% maior que a geral,
 * seasonalIndex = 1,2 e o forecast de março = 1.000×1,2 = 1.200 m.
 * São exigidos 24 meses para observar cada mês em pelo menos dois ciclos.
 */
const calcularTendenciaSazonal = (historicalValues, startMonth) => {
  const historyLength = historicalValues.length;
  const xAverage = (historyLength - 1) / 2;
  const yAverage = average(historicalValues);
  const denominator = historicalValues.reduce((sum, value, index) => sum + ((index - xAverage) ** 2), 0);
  const slope = denominator
    ? historicalValues.reduce((sum, value, index) => sum + (index - xAverage) * (value - yAverage), 0) / denominator
    : 0;
  const intercept = yAverage - slope * xAverage;
  const seasonalIndexes = Array.from({ length: 12 }, (_, month) => {
    const monthValues = historicalValues.filter(
      (value, index) => (startMonth + index) % 12 === month,
    );
    return monthValues.length && yAverage > 0
      ? average(monthValues) / yAverage
      : 1;
  });
  const seasonalAverage = average(seasonalIndexes) || 1;
  const normalizedIndexes = seasonalIndexes.map((value) => value / seasonalAverage);
  const fittedValues = historicalValues.map((value, index) =>
    Math.max(0, (intercept + slope * index) * normalizedIndexes[(startMonth + index) % 12]));

  return {
    fitted: fittedValues,
    forecast: (forecastHorizon) => Array.from({ length: forecastHorizon }, (_, offset) => {
      const index = historyLength + offset;
      return Math.max(0, (intercept + slope * index) * normalizedIndexes[(startMonth + index) % 12]);
    }),
  };
};

/*
 * Holt-Winters aditivo mantém level, trend e seasonal component.
 * O forecast é level + passos×trend + seasonal. Exemplo: level 1.000,
 * trend 50 e efeito sazonal 100 produzem 1.000 + 1×50 + 100 = 1.150 m.
 * O código usa alpha 0,3, beta 0,1, gamma 0,1 e ciclo mensal de 12 meses.
 */
const calcularHoltWinters = (historicalValues, seasonLength = 12) => {
  if (historicalValues.length < seasonLength * 2) return { fitted: [], forecast: () => [] };
  const levelAlpha = 0.3;
  const trendBeta = 0.1;
  const seasonalGamma = 0.1;
  let currentLevel = average(historicalValues.slice(0, seasonLength));
  let currentTrend = (
    average(historicalValues.slice(seasonLength, seasonLength * 2)) - currentLevel
  ) / seasonLength;
  const seasonalComponents = historicalValues
    .slice(0, seasonLength)
    .map((value) => value - currentLevel);
  const fittedValues = [];

  historicalValues.forEach((value, index) => {
    const seasonalComponent = seasonalComponents[index % seasonLength] || 0;
    fittedValues[index] = index
      ? Math.max(0, currentLevel + currentTrend + seasonalComponent)
      : value;
    const previousLevel = currentLevel;
    currentLevel = levelAlpha * (value - seasonalComponent)
      + (1 - levelAlpha) * (currentLevel + currentTrend);
    currentTrend = trendBeta * (currentLevel - previousLevel)
      + (1 - trendBeta) * currentTrend;
    seasonalComponents[index % seasonLength] = seasonalGamma * (value - currentLevel)
      + (1 - seasonalGamma) * seasonalComponent;
  });

  return {
    fitted: fittedValues,
    forecast: (forecastHorizon) => Array.from({ length: forecastHorizon }, (_, offset) =>
      Math.max(0, currentLevel + (offset + 1) * currentTrend
        + seasonalComponents[(historicalValues.length + offset) % seasonLength])),
  };
};

export const calcularPrevisoes = (monthlySeries, forecastHorizon, movingWindow, smoothingAlpha) => {
  const historicalValues = monthlySeries.map((item) => item.value);
  const validMovingWindow = Math.min(movingWindow, Math.max(historicalValues.length, 1));
  const mediaSimples = calcularMediaMovel(historicalValues, validMovingWindow);
  const mediaPonderada = calcularMediaMovel(historicalValues, validMovingWindow, true);
  const suavizacao = calcularSuavizacaoExponencial(historicalValues, smoothingAlpha);
  const startMonth = monthlySeries[0]?.date.getUTCMonth() || 0;
  const tendenciaSazonal = historicalValues.length >= 24
    ? calcularTendenciaSazonal(historicalValues, startMonth)
    : null;
  const holtWinters = historicalValues.length >= 24
    ? calcularHoltWinters(historicalValues)
    : null;
  const previsoes = {
    simple: { label: "MMS", ...mediaSimples, future: mediaSimples.forecast(forecastHorizon) },
    weighted: { label: "MMP", ...mediaPonderada, future: mediaPonderada.forecast(forecastHorizon) },
    exponential: { label: "SES", ...suavizacao, future: suavizacao.forecast(forecastHorizon) },
    seasonal: tendenciaSazonal
      ? { label: "Tendência sazonal", ...tendenciaSazonal, future: tendenciaSazonal.forecast(forecastHorizon) }
      : null,
    winters: holtWinters
      ? { label: "Holt-Winters", ...holtWinters, future: holtWinters.forecast(forecastHorizon) }
      : null,
  };

  Object.values(previsoes).filter(Boolean).forEach((method) => {
    method.mape = calcularMape(historicalValues, method.fitted);
  });

  return previsoes;
};

export const buildForecastLabels = (monthlySeries, forecastHorizon) => {
  if (!monthlySeries.length) return [];
  const lastDate = monthlySeries[monthlySeries.length - 1].date;
  return Array.from(
    { length: forecastHorizon },
    (_, index) => monthFormatter.format(addMonths(lastDate, index + 1)),
  );
};

export const calcularResumoHistorico = (monthlySeries) => {
  if (!monthlySeries.length) return null;
  const peak = monthlySeries.reduce((current, item) => item.value > current.value ? item : current);
  const valley = monthlySeries.reduce((current, item) => item.value < current.value ? item : current);
  return {
    average: average(monthlySeries.map((item) => item.value)),
    months: monthlySeries.length,
    peak,
    valley,
    lastThreeAverage: average(monthlySeries.slice(-3).map((item) => item.value)),
  };
};
